import { Badge } from '@/components/Shared/Badge';
import { StampTime } from '@/components/Shared/StampTime';
import { sortEventsByTime } from '@/store/useTimeclockStore';
import {
  FacilityFlowCoreModulesPlanningEntitiesTimeClockDayStatus as TimeClockDayStatusEnum,
  type FacilityFlowCoreModulesPlanningWorkflowsTimeClockTimeClockEventDto as TimeClockEvent,
} from '@/api/generated/models';
import { eventBadgeConfig, type DescribeChoices, type TFn } from './timeClockFormat';

/** "Bestätigt" or "Offen" for a time clock day. */
export function DayStatusBadge({ status, t }: { status: number | undefined; t: TFn }) {
  if (status === TimeClockDayStatusEnum.NUMBER_2) {
    return <Badge variant="success">{t('status.confirmed')}</Badge>;
  }
  return <Badge variant="warning">{t('status.open')}</Badge>;
}

export function EventBadges({
  events,
  compact = false,
  t,
  describeChoices,
}: {
  events?: TimeClockEvent[] | null;
  compact?: boolean;
  t: TFn;
  describeChoices: DescribeChoices;
}) {
  const sortedEvents = sortEventsByTime(events);
  const choices = describeChoices(sortedEvents);

  if (sortedEvents.length === 0) {
    return <span className="text-[12px] text-slate-400">–</span>;
  }

  return (
    <div className={`flex flex-wrap ${compact ? 'gap-1' : 'gap-1.5'}`}>
      {sortedEvents.map((evt, idx) => {
        const config = evt.eventType ? eventBadgeConfig[evt.eventType] : null;
        const choice = choices[idx];
        return (
          <span
            key={evt.timeClockEventId ?? idx}
            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[11px] font-medium tabular-nums ${config?.color ?? 'bg-slate-100 text-slate-600 border border-slate-200'}`}
            title={evt.isSystemGenerated ? t('events.automatic') : undefined}
          >
            <StampTime event={evt} />
            <span className="opacity-70">{compact ? (config?.icon ?? '•') : (config ? t(config.shortKey) : '?')}</span>
            {evt.isSystemGenerated && <span className="font-normal opacity-80">{t('events.automaticShort')}</span>}
            {choice && <span className="font-normal opacity-80">· {choice}</span>}
          </span>
        );
      })}
    </div>
  );
}
